import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { BottomNavigation } from './components/layout/BottomNavigation';
import { HomePage } from './pages/HomePage';
import { ExplorePage } from './pages/ExplorePage';
import { HashtagPage } from './pages/HashtagPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { EditProfilePage } from './pages/EditProfilePage';
import { CreatePostPage } from './pages/CreatePostPage';
import { SavedPostsPage } from './pages/SavedPostsPage';
import { FollowersPage } from './pages/FollowersPage';
import { MessagesPage } from './pages/MessagesPage';
import { ConversationPage } from './pages/ConversationPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ConfirmPage } from './pages/ConfirmPage';
import { LogoutPage } from './pages/LogoutPage';
import { TestUserPage } from './pages/TestUserPage';
import { useAuthStore } from './store/authStore';
import { supabase } from './lib/supabase';

function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setLoading]);

  const { user, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {user && <Header />}
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route
            path="/signup"
            element={user ? <Navigate to="/" replace /> : <SignupPage />}
          />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/test-user" element={<TestUserPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={user ? <HomePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/explore"
            element={user ? <ExplorePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/hashtag/:hashtag"
            element={user ? <HashtagPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/notifications"
            element={user ? <NotificationsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile"
            element={user ? <ProfilePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile/:username"
            element={user ? <ProfilePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile/:username/:type"
            element={user ? <FollowersPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/edit-profile"
            element={user ? <EditProfilePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/create"
            element={user ? <CreatePostPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/saved"
            element={user ? <SavedPostsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/messages"
            element={user ? <MessagesPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/messages/:conversationId"
            element={user ? <ConversationPage /> : <Navigate to="/login" replace />}
          />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
        </Routes>
        {user && <BottomNavigation />}
      </div>
    </Router>
  );
}

export default App;